import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Stack,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tooltip,
  Alert,
  Skeleton,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Analytics as AnalyticsIcon,
  BoltOutlined as AutomationIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import automationService from '../../services/automationService';
import StatsCard from '../../components/core/cards/StatsCard';
import EntityEmptyState from '../../components/core/EntityEmptyState';

const AutomationDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = id; // The project ID is the 'id' param from the parent route
  
  // Try to get project from outlet context if available
  let contextData = {};
  try {
    contextData = useOutletContext() || {};
  } catch (e) {
    // If not in outlet context, that's okay
  }
  
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMenu, setFilterMenu] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowMenu, setWorkflowMenu] = useState(null);

  // Fetch workflows and stats
  useEffect(() => {
    loadDashboardData();
  }, [projectId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [workflowsRes, statsRes] = await Promise.all([
        automationService.getWorkflows(projectId),
        automationService.getAnalytics(projectId)
      ]);
      
      setWorkflows(workflowsRes.data.workflows);
      setStats(statsRes.data.analytics);
    } catch (error) {
      console.error('Error loading automation dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    navigate(`/projects/${projectId}/automation/workflows/new`);
  };

  const handleEditWorkflow = (workflowId) => {
    navigate(`/projects/${projectId}/automation/workflows/${workflowId}/edit`);
  };

  const handleViewWorkflow = (workflowId) => {
    navigate(`/projects/${projectId}/automation/workflows/${workflowId}`);
  };

  const handleToggleWorkflow = async (workflowId, enabled) => {
    try {
      await automationService.toggleWorkflow(workflowId, enabled);
      await loadDashboardData();
    } catch (error) {
      console.error('Error toggling workflow:', error);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      await automationService.deleteWorkflow(workflowId);
      await loadDashboardData();
    } catch (error) {
      console.error('Error deleting workflow:', error);
    }
  };

  const handleDuplicateWorkflow = async (workflowId) => {
    try {
      const exportRes = await automationService.exportWorkflow(workflowId);
      const importRes = await automationService.importWorkflow(projectId, {
        workflow: exportRes.data.workflow,
        name: `${exportRes.data.workflow.name} (Copy)`
      });
      await loadDashboardData();
    } catch (error) {
      console.error('Error duplicating workflow:', error);
    }
  };

  const filteredWorkflows = workflows.filter(workflow => {
    if (searchTerm) {
      return workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             workflow.description?.toLowerCase().includes(searchTerm.toLowerCase());
    }
    
    if (selectedTab === 1) return workflow.settings?.enabled;
    if (selectedTab === 2) return !workflow.settings?.enabled;
    if (selectedTab === 3) return workflow.executionStats?.recentFailures > 0;
    
    return true;
  });

  const getWorkflowStatus = (workflow) => {
    if (!workflow.settings?.enabled) return 'disabled';
    if (workflow.executionStats?.recentFailures > 0) return 'error';
    if (workflow.executionStats?.successRate < 80) return 'warning';
    return 'success';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <SuccessIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'error': return <ErrorIcon color="error" />;
      default: return <PauseIcon color="disabled" />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Workflow Automation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage automated workflows for your project
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateWorkflow}
          >
            New Workflow
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Total Workflows"
              value={workflows.length}
              icon={<AutomationIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Active Workflows"
              value={workflows.filter(w => w.settings?.enabled).length}
              icon={<PlayIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Executions Today"
              value={stats.executions?.totalExecutions || 0}
              icon={<ScheduleIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatsCard
              title="Success Rate"
              value={`${Math.round(stats.executions?.successRate || 0)}%`}
              icon={<AnalyticsIcon />}
              color={stats.executions?.successRate >= 80 ? 'success' : 'warning'}
            />
          </Grid>
        </Grid>
      )}

      {/* Filters and Search */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
            <Tab label={`All (${workflows.length})`} />
            <Tab label={`Active (${workflows.filter(w => w.settings?.enabled).length})`} />
            <Tab label={`Inactive (${workflows.filter(w => !w.settings?.enabled).length})`} />
            <Tab 
              label={
                <Badge 
                  badgeContent={workflows.filter(w => w.executionStats?.recentFailures > 0).length} 
                  color="error"
                >
                  <span>Issues</span>
                </Badge>
              } 
            />
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <IconButton onClick={(e) => setFilterMenu(e.currentTarget)}>
              <FilterIcon />
            </IconButton>
          </Stack>
        </Box>
      </Paper>

      {/* Workflows Grid */}
      {filteredWorkflows.length === 0 ? (
        <EntityEmptyState
          title="No workflows found"
          description={searchTerm ? "Try adjusting your search" : "Create your first workflow to get started"}
          icon={<AutomationIcon sx={{ fontSize: 48 }} />}
          actions={
            !searchTerm && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateWorkflow}
              >
                Create Workflow
              </Button>
            )
          }
        />
      ) : (
        <Grid container spacing={3}>
          {filteredWorkflows.map((workflow) => {
            const status = getWorkflowStatus(workflow);
            
            return (
              <Grid item xs={12} sm={6} md={4} key={workflow._id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 }
                  }}
                  onClick={() => handleViewWorkflow(workflow._id)}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {workflow.name}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getStatusIcon(status)}
                          <Chip
                            label={workflow.settings?.enabled ? 'Active' : 'Inactive'}
                            color={workflow.settings?.enabled ? 'success' : 'default'}
                            size="small"
                          />
                          {workflow.trigger?.type && (
                            <Chip
                              label={workflow.trigger.type.replace(/_/g, ' ')}
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Stack>
                      </Box>
                      <IconButton 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedWorkflow(workflow);
                          setWorkflowMenu(e.currentTarget);
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {workflow.description || 'No description'}
                    </Typography>

                    {workflow.executionStats && (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Executions
                          </Typography>
                          <Typography variant="caption">
                            {workflow.executionStats.total || 0}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Success Rate
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color={workflow.executionStats.successRate >= 80 ? 'success.main' : 'warning.main'}
                          >
                            {Math.round(workflow.executionStats.successRate || 0)}%
                          </Typography>
                        </Stack>
                        {workflow.executionStats.successRate < 100 && (
                          <LinearProgress 
                            variant="determinate" 
                            value={workflow.executionStats.successRate || 0}
                            color={workflow.executionStats.successRate >= 80 ? 'success' : 'warning'}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<EditIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditWorkflow(workflow._id);
                      }}
                    >
                      Edit
                    </Button>
                    <Button 
                      size="small"
                      startIcon={workflow.settings?.enabled ? <PauseIcon /> : <PlayIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleWorkflow(workflow._id, !workflow.settings?.enabled);
                      }}
                    >
                      {workflow.settings?.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Workflow Menu */}
      <Menu
        anchorEl={workflowMenu}
        open={Boolean(workflowMenu)}
        onClose={() => {
          setWorkflowMenu(null);
          setSelectedWorkflow(null);
        }}
      >
        <MenuItem onClick={() => {
          handleEditWorkflow(selectedWorkflow?._id);
          setWorkflowMenu(null);
        }}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          handleDuplicateWorkflow(selectedWorkflow?._id);
          setWorkflowMenu(null);
        }}>
          <CopyIcon sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        <MenuItem onClick={() => {
          handleToggleWorkflow(selectedWorkflow?._id, !selectedWorkflow?.settings?.enabled);
          setWorkflowMenu(null);
        }}>
          {selectedWorkflow?.settings?.enabled ? (
            <>
              <PauseIcon sx={{ mr: 1 }} /> Disable
            </>
          ) : (
            <>
              <PlayIcon sx={{ mr: 1 }} /> Enable
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => {
          handleDeleteWorkflow(selectedWorkflow?._id);
          setWorkflowMenu(null);
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default AutomationDashboard;